import { useRef, useState } from "react";
import { useCameraStream } from "../hooks/useCameraStream";
import { updateCameraProfile, type CameraInfo, type CameraProfile } from "../hooks/useCameras";

const RESOLUTION_PRESETS: { label: string; width: number; height: number }[] = [
  { label: "320p", width: 320, height: 240 },
  { label: "480p", width: 640, height: 480 },
  { label: "720p", width: 1280, height: 720 },
];

const FPS_PRESETS = [5, 8, 10, 15];

interface CameraFeedProps {
  camera: CameraInfo;
  streamBase: string;
  onProfileChanged: () => void;
}

function streamUrl(streamBase: string, cameraId: string): string {
  return `${streamBase}?cam=${encodeURIComponent(cameraId)}`;
}

function matchesResolution(profile: CameraProfile | undefined, width: number, height: number): boolean {
  return profile?.width === width && profile?.height === height;
}

export function CameraFeed({ camera, streamBase, onProfileChanged }: CameraFeedProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const offline = camera.status !== "online";
  const { connected, stale } = useCameraStream({
    wsUrl: streamUrl(streamBase, camera.id),
    enabled: !offline,
    canvasRef,
  });

  const applyProfile = async (patch: Partial<CameraProfile>) => {
    setSaving(true);
    setProfileError(null);
    const result = await updateCameraProfile(camera.id, patch);
    setSaving(false);
    if (!result.ok) {
      setProfileError(result.error);
      return;
    }
    onProfileChanged();
  };

  const overlayLabel = offline
    ? "Offline"
    : !connected
      ? "Connecting…"
      : stale
        ? "Reconnecting…"
        : null;

  return (
    <div className="camera-feed">
      <div className="camera-feed__viewport">
        <canvas ref={canvasRef} className="camera-feed__canvas" />
        {overlayLabel && (
          <div
            className={`camera-feed__overlay${offline ? " camera-feed__overlay--offline" : " camera-feed__overlay--stale"}`}
          >
            {!offline && <span className="camera-feed__spinner" aria-hidden="true" />}
            <span className="camera-feed__overlay-text">{overlayLabel}</span>
          </div>
        )}
        <div className="camera-feed__badge">
          <span
            className={`camera-feed__dot${connected && !stale && !offline ? " camera-feed__dot--live" : ""}`}
          />
          {camera.id}
        </div>
      </div>

      <div className="camera-feed__controls">
        <div className="camera-feed__control-group" role="group" aria-label="Resolution">
          {RESOLUTION_PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              className={`camera-feed__chip${matchesResolution(camera.effectiveProfile, preset.width, preset.height) ? " camera-feed__chip--active" : ""}`}
              disabled={saving || offline}
              onClick={() => void applyProfile({ width: preset.width, height: preset.height })}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div className="camera-feed__control-group" role="group" aria-label="Frame rate">
          {FPS_PRESETS.map((fps) => (
            <button
              key={fps}
              type="button"
              className={`camera-feed__chip${camera.effectiveProfile?.fps === fps ? " camera-feed__chip--active" : ""}`}
              disabled={saving || offline}
              onClick={() => void applyProfile({ fps })}
            >
              {fps}fps
            </button>
          ))}
        </div>
      </div>

      {profileError && <div className="camera-feed__error">{profileError}</div>}
    </div>
  );
}
